/**
 * Shared class sets for design-system v1 controls (tokens from styles/tokens.css).
 * Complete literal strings on purpose, so Tailwind generates every class.
 */

const btnBase =
  'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md text-sm font-medium tracking-normal ' +
  'transition-colors duration-fast disabled:opacity-50 disabled:cursor-not-allowed';

/** One primary per view: submits or creates. */
export const btnPrimary = `${btnBase} bg-primary text-primary-fg hover:bg-primary-hover`;
/** Default for most actions. */
export const btnSecondary = `${btnBase} bg-surface text-fg border border-line-strong hover:bg-surface-2`;
/** Toolbars and row actions. */
export const btnGhost = `${btnBase} text-fg-muted hover:bg-surface-2 hover:text-fg`;
/** Delete, reject, revoke (solid only inside a confirmation dialog). */
export const btnDanger = `${btnBase} text-danger border border-danger/30 hover:bg-danger-subtle`;

export const selectCls =
  'h-8 py-0 pl-2.5 pr-8 rounded-md bg-surface text-fg border border-line-strong text-sm focus:border-ring';

/** Segmented control (view switches and section tabs): neutral, never brand colours. */
export const segGroup = 'inline-flex max-w-full overflow-x-auto gap-0.5 p-0.5 rounded-lg bg-surface-2';
export const segItem = 'inline-flex items-center gap-1.5 h-7 px-3 shrink-0 whitespace-nowrap rounded-md text-sm transition-colors duration-fast';
export const segItemOn = `${segItem} bg-surface-3 text-fg font-medium shadow-xs`;
export const segItemOff = `${segItem} text-fg-muted hover:text-fg`;

/** Card surface: flat with a line border; hover lift only when the whole card is a link. */
export const card = 'rounded-lg border border-line bg-surface';
export const cardLink = `${card} transition-colors duration-fast hover:border-line-strong hover:bg-surface-2`;

/**
 * Page frame (Sep 2026 standard): EVERY page is left-aligned with the sidebar,
 * one gutter, one width. `pageFrame` (1440px) for dashboards, lists and detail
 * pages; `formFrame` (720px, same left edge) only for single-column forms.
 * Never centre a page with mx-auto and never pick another max-w for a page root.
 */
export const pageFrame = 'w-full max-w-page px-4 md:px-8 pt-6 pb-10';
export const formFrame = 'w-full max-w-form px-4 md:px-8 pt-6 pb-10';
export const contentMax = 'max-w-content';
export const formMax = 'max-w-form';

/**
 * Stat grid (Sep 2026): label + value tiles that reflow by available width
 * (min 9rem per tile), so long amounts like "CHF 452'072.83" never touch their
 * neighbour in a narrow card. Give each tile `min-w-0`.
 */
export const statGrid = 'grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-x-6 gap-y-3';

/**
 * Type scale (Sep 2026) — one role per size:
 *   text-2xl  page title (PageHeader h1) and KPI values
 *   text-lg   section titles on a page
 *   text-sm   body, table cells, controls, card titles (font-medium): the default
 *   text-xs   meta lines, helper text, secondary cell lines
 *   text-2xs  overline labels, table headers, badges (uppercase)
 */
export const kpiValue = 'text-2xl font-semibold tabular-nums text-fg';
export const sectionTitle = 'font-sans text-lg leading-7 font-semibold tracking-tighter text-body';
export const overline = 'text-2xs font-semibold uppercase text-fg-faint';
